import React from 'react';
import { useNavigate } from 'react-router-dom';
import { markNotificationAsRead } from '../../services/notificationService';
import Button from '../common/Button';
import { Caption, Typography } from '../common/Typography';
import CollapsibleNotificationItem from '../notifications/CollapsibleNotificationItem';

/**
 * GuestMergeDeclinedNotificationItem Component
 * Notification item for declined guest merge requests with link to guest management
 */
const GuestMergeDeclinedNotificationItem = ({ 
  notification, 
  currentUser,
  onUpdate,
  onClose 
}) => {
  const navigate = useNavigate();
  const notificationData = notification.data || {};
  const clubId = notificationData.club_id;
  const clubName = notificationData.club_name || 'the club';
  const userName = notificationData.user_name || 'A user';
  const userEmail = notificationData.user_email || '';
  const userMessage = notificationData.user_message;
  const guestSummary = notificationData.guest_summary || {};

  const handleGoToGuestManagement = async () => {
    // Mark notification as read
    if (notification.id && !notification.is_read) {
      try {
        await markNotificationAsRead(notification.id);
      } catch (err) {
        console.error('Error marking notification as read:', err);
      }
    }

    // Close notifications modal
    if (onClose) {
      onClose();
    }

    // Navigate to club admin guest management page
    if (clubId) {
      navigate(`/club/${clubId}/settings`, {
        state: {
          activeTab: 'guest-management',
          selectedClub: { id: clubId }
        }
      });
    }

    // Update notifications list
    if (onUpdate) {
      onUpdate();
    }
  };

  const detailsContent = (
    <>
      {/* Guest Summary Details */}
      {guestSummary && Object.keys(guestSummary).length > 0 && (
        <div>
          <Typography variant="strong" className="text-sm mb-2">Guest Account Details:</Typography>
          <div className="space-y-1 text-sm">
            {guestSummary.guest_name && (
              <div>
                <span className="text-text-muted">Guest Name: </span>
                <Typography variant="strong" as="span">{guestSummary.guest_name}</Typography>
              </div>
            )}
            {guestSummary.guest_email && (
              <div>
                <span className="text-text-muted">Assigned Email: </span>
                <Typography variant="strong" as="span">{guestSummary.guest_email}</Typography>
              </div>
            )}
            {guestSummary.games_count !== undefined && (
              <div>
                <span className="text-text-muted">Games: </span>
                <Typography variant="strong" as="span">{guestSummary.games_count}</Typography>
              </div>
            )}
          </div>
        </div>
      )}

      {/* User Message */}
      {userMessage && (
        <div className="bg-info-light border border-border-medium rounded-lg p-3">
          <Typography variant="strong" className="text-sm mb-1">User Message:</Typography>
          <p className="text-sm text-text-secondary mt-1">{userMessage}</p>
        </div>
      )}

      {/* User Info */}
      <div className="text-sm">
        <span className="text-text-muted">User: </span>
        <Typography variant="strong" as="span">{userName}</Typography>
      </div>
      {userEmail && (
        <div className="text-sm">
          <span className="text-text-muted">Email: </span>
          <Typography variant="strong" as="span">{userEmail}</Typography>
        </div>
      )}
    </>
  );

  const metadata = clubName && (
    <Caption>Club: {clubName}</Caption>
  );

  return (
    <CollapsibleNotificationItem
      notification={notification}
      borderColor="amber"
      badgeVariant="warning"
      detailsContent={detailsContent}
      metadata={metadata}
    >
      <Button
        variant="primary"
        size="small"
        onClick={handleGoToGuestManagement}
      >
        Go to Guest Management
      </Button>
    </CollapsibleNotificationItem>
  );
};

export default GuestMergeDeclinedNotificationItem;

